import {
  SORT_PRESETS, applyFilters, facetOptions, hidePastWeeks, sortRows,
  type Facet, type FacetOption, type ListState, type SortableRow, type WeekRow,
} from '@region13/shared-types'

/** PURE. Hide past weeks → options → filter → sort, in that order.
 *
 *  ⚠️ OPTIONS COME FROM `scoped` (after hiding), NOT FROM `rows`: a hidden past
 *  week offered in the picker would select to an empty list. A value selected
 *  from an old link stays listed anyway, labeled "(no games)".
 *
 *  `filterFacets` (default `facets`) narrows which facets filter here, for a
 *  screen that applies some facets at another level (Slots: role and state per
 *  game). Options are still built for every facet in `facets`. `state.sort`
 *  null keeps the input order. */
export function listView<Row extends WeekRow & SortableRow>(args: {
  rows: readonly Row[]
  facets: readonly Facet<Row>[]
  filterFacets?: readonly Facet<Row>[]
  state: ListState
  /** Absent: Hide past weeks cannot apply (a screen with no such toggle). */
  today?: string
}): { visible: Row[]; scoped: Row[]; options: Record<string, FacetOption[]> } {
  const scoped = args.state.hidePast && args.today !== undefined ? hidePastWeeks(args.rows, args.today) : [...args.rows]
  const options: Record<string, FacetOption[]> = {}
  // ⚠️ A FACET WITH A `rebuild` HOOK (the week facet) IS RE-BUILT FROM `scoped`,
  // NOT USED AS PASSED. Its labels come from the rows (the week label carries
  // its earliest DATED row), and a caller builds it once from every loaded row
  // so it survives a later toggle back to "show past". Rebuilding from
  // `scoped` is what makes a week with its only dated row hidden read as
  // "Week N" instead of stale-dated.
  for (const f of args.facets) {
    const live = f.rebuild ? f.rebuild(scoped) : f
    options[f.key] = facetOptions(scoped, live, args.state.selection[f.key] ?? [])
  }
  const filtered = applyFilters(scoped, args.filterFacets ?? args.facets, args.state.selection)
  const visible = args.state.sort ? sortRows(filtered, SORT_PRESETS[args.state.sort].keys) : filtered
  return { visible, scoped, options }
}

/** The empty-list line. On the default view with Hide past weeks on, the
 *  usual cause is that every game is in a past week, so say that and how to
 *  see them; otherwise the filters are why. */
export function emptyListMessage(listState: { state: ListState; isDefault: boolean }): string {
  return listState.isDefault && listState.state.hidePast
    ? 'No games this week or later. Untick Hide past weeks to see earlier games.'
    : 'No games match these filters.'
}
